import { App, Alert, Space } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  bulkDeleteRoutes,
  bulkRecomputeRoutes,
  listRouteIds,
} from '@/api/endpoints/routes';
import RoutesLibraryFilters from '@/components/routes/RoutesLibraryFilters';
import RoutesLibraryTable, {
  type RoutesLibraryTableHandle,
  type VariantToggleHandlers,
} from '@/components/routes/RoutesLibraryTable';
import { useRoutesLibrary } from '@/hooks/useRoutesLibrary';
import { useRouteWorkspace } from '@/stores/RouteWorkspaceContext';
import { useTileRevision } from '@/stores/TileRevisionContext';
import type { RouteResponse } from '@/types/routes';
import { extractAxiosMessage } from '@/utils/axiosError';
import { chunk } from '@/utils/chunk';

// Mirrors the backend per-request cap (_BULK_MAX_IDS in routes router).
const BULK_MAX_IDS = 500;

// Above this many selected routes we stop mirroring the selection onto the
// map: drawing hundreds of polylines (plus per-barrier detour fetches) is
// unreadable and would blow past the geometry-batch cap (500). Selection
// still drives bulk actions — only the map preview is suppressed.
const MAP_RENDER_CAP = 50;

interface WorkspaceRoutesLibraryProps {
  /** Called when the user re-clicks a row already selected — forces the
   * property sheet suppression to release. */
  readonly onRowReopen: () => void;
}

/**
 * Routes-mode body of the workspace panel: filters + paginated table +
 * bulk actions. Pulls its own data via `useRoutesLibrary`; everything
 * above (segmented switcher, property sheet, draft form) is handled by
 * the parent `WorkspacePanel`.
 */
export default function WorkspaceRoutesLibrary({
  onRowReopen,
}: WorkspaceRoutesLibraryProps) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const {
    focusedRouteId,
    focusRoute,
    setVisibleRouteIds,
    getVariantToggles,
    setVariantToggle,
  } = useRouteWorkspace();
  const { bumpRoutes } = useTileRevision();

  const library = useRoutesLibrary();
  const { refresh: refreshLibrary, appliedFilters } = library;
  const [selectedKeys, setSelectedKeys] = useState<readonly string[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectAllBusy, setSelectAllBusy] = useState(false);
  const tableRef = useRef<RoutesLibraryTableHandle>(null);

  // Push multi-select into the shared workspace so map layers render the
  // chosen routes — but only up to MAP_RENDER_CAP. Beyond it (e.g. after
  // "select all across pages") we keep the selection for bulk actions yet
  // suppress the map preview to avoid an unreadable flood and the
  // geometry-batch 500-id cap.
  const mapRenderSuppressed = selectedKeys.length > MAP_RENDER_CAP;
  useEffect(() => {
    setVisibleRouteIds(mapRenderSuppressed ? [] : selectedKeys);
  }, [mapRenderSuppressed, selectedKeys, setVisibleRouteIds]);

  // Scroll to the focused row when selection comes from the map.
  useEffect(() => {
    if (focusedRouteId !== null) {
      tableRef.current?.scrollToRoute(focusedRouteId);
    }
  }, [focusedRouteId]);

  const handleRowClick = useCallback(
    (route: RouteResponse) => {
      onRowReopen();
      focusRoute(route.route_id);
      setSelectedKeys((prev) =>
        prev.includes(route.route_id) ? prev : [...prev, route.route_id],
      );
    },
    [focusRoute, onRowReopen],
  );

  const handleSelectAllAcrossPages = useCallback(async () => {
    setSelectAllBusy(true);
    try {
      const result = await listRouteIds({ ...appliedFilters });
      setSelectedKeys(result.route_ids);
    } catch (error) {
      message.error(
        extractAxiosMessage(error) ?? t('routes.library.bulk.selectAllError'),
      );
    } finally {
      setSelectAllBusy(false);
    }
  }, [appliedFilters, message, t]);

  const handleClearSelection = useCallback(() => setSelectedKeys([]), []);

  const handleBulkDelete = useCallback(async () => {
    if (selectedKeys.length === 0) return;
    setBulkBusy(true);
    try {
      const batches = chunk(selectedKeys, BULK_MAX_IDS);
      const results = await Promise.all(batches.map((b) => bulkDeleteRoutes(b)));
      const deleted = results.reduce((sum, r) => sum + r.deleted, 0);
      message.success(t('routes.library.bulk.deleteResult', { count: deleted }));
      setSelectedKeys([]);
      bumpRoutes();
      refreshLibrary();
    } catch (error) {
      message.error(
        extractAxiosMessage(error) ?? t('routes.library.bulk.deleteError'),
      );
    } finally {
      setBulkBusy(false);
    }
  }, [bumpRoutes, message, refreshLibrary, selectedKeys, t]);

  const handleBulkRecompute = useCallback(async () => {
    if (selectedKeys.length === 0) return;
    setBulkBusy(true);
    try {
      const batches = chunk(selectedKeys, BULK_MAX_IDS);
      const results = await Promise.all(
        batches.map((b) => bulkRecomputeRoutes(b)),
      );
      const marked = results.reduce((sum, r) => sum + r.marked_stale, 0);
      message.success(
        t('routes.library.bulk.recomputeResult', { count: marked }),
      );
      setSelectedKeys([]);
    } catch (error) {
      message.error(
        extractAxiosMessage(error) ?? t('routes.library.bulk.recomputeError'),
      );
    } finally {
      setBulkBusy(false);
    }
  }, [message, selectedKeys, t]);

  const toggles = useMemo<VariantToggleHandlers>(
    () => ({
      getToggles: getVariantToggles,
      setToggle: setVariantToggle,
    }),
    [getVariantToggles, setVariantToggle],
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <RoutesLibraryFilters
        filters={library.filters}
        onChange={library.setFilters}
        onReset={library.resetFilters}
        onRefresh={library.refresh}
        disabled={library.status === 'loading'}
      />
      {library.status === 'error' && (
        <Alert
          type="error"
          showIcon
          message={t('routes.library.loadError')}
          description={library.errorMessage ?? undefined}
        />
      )}
      {mapRenderSuppressed && (
        <Alert
          type="info"
          showIcon
          message={t('routes.library.mapRenderSuppressed', {
            count: selectedKeys.length,
            cap: MAP_RENDER_CAP,
          })}
        />
      )}
      <RoutesLibraryTable
        ref={tableRef}
        items={library.items}
        total={library.total}
        page={library.page}
        pageSize={library.pageSize}
        loading={library.status === 'loading'}
        selectedRowKeys={selectedKeys}
        focusedRouteId={focusedRouteId}
        bulkBusy={bulkBusy}
        selectAllBusy={selectAllBusy}
        toggles={toggles}
        sort={library.sort}
        onSortChange={library.setSort}
        onPageChange={library.setPage}
        onSelectionChange={setSelectedKeys}
        onSelectAllAcrossPages={handleSelectAllAcrossPages}
        onClearSelection={handleClearSelection}
        onRowClick={handleRowClick}
        onBulkDelete={handleBulkDelete}
        onBulkRecompute={handleBulkRecompute}
      />
    </Space>
  );
}
